import { NModalLayoutRight } from '../../../../common/modal/layout-right/modal-layout-right.component';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ISizeName } from '@df/core/service/client';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { TranslationModule } from '@df/ui/translation/translation.module';

export namespace NProductViewImageSizesModal {
    export const BLOCK_ID = 'product-view-image-sizes-modal';

    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IBlockDataContent extends NICmsContentComponent.IData {
        title: string;
        tableTitle: string;
        rows: {
            size: string;
            thickness: string;
            imageSizeWithBleed: string;
            minimumPixels: string;
            recommendedPixels: string;
        }[];
        points: {
            text: string;
        }[];
    }
}

@DfCmsModal({
    id: NProductViewImageSizesModal.BLOCK_ID,
    name: 'Product View Image Sizes Modal',
    position: (sizeName: ISizeName) => (sizeName === 'S' ? 'bottom' : 'right'),
    config: sizeName => ({
        ...NModalLayoutRight.getConfig(sizeName),
        width: NModalLayoutRight.getWidth(sizeName),
        height: sizeName === 'S' ? '85dvh' : '100dvh',
        position:
            sizeName === 'S'
                ? { bottom: '0' }
                : {
                      top: '0',
                      right: '0',
                      bottom: '0'
                  },
        hasBackdrop: true
    }),
    autoCreate: true,
    reopenPolicy: NDfModal.ReopenPolicy.last,
    sizeRestriction: sizeId => (['S', 'M'].includes(sizeId) ? ['S', 'M'] : ['L', 'X']),
    prerenderAllowed: false
})
@ICmsComponent<ProductViewImageSizesModalComponent>({
    id: NProductViewImageSizesModal.BLOCK_ID,
    block: {
        url: NProductViewImageSizesModal.BLOCK_ID,
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    name: 'Product View Image Sizes Modal',
    description: 'Product View Image Sizes Modal',
    fields: [
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true
        },
        {
            key: 'tableTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Table Title',
            required: true
        },
        {
            key: 'pointsTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Points Title',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            key: 'points',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Points',
            itemName: 'Point',
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Text',
                    required: true
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            key: 'rows',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Table Rows',
            itemName: 'Row',
            fields: [
                {
                    key: 'size',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Size',
                    required: true
                },
                {
                    key: 'thickness',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Thickness',
                    required: true
                },
                {
                    key: 'imageSizeWithBleed',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Image Size With Bleed',
                    required: true
                },
                {
                    key: 'minimumPixels',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Minimum Pixels',
                    required: true
                },
                {
                    key: 'recommendedPixels',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Recommended Pixels',
                    required: true
                }
            ]
        }
    ]
})
@Component({
    selector: 'product-view-image-sizes-modal',
    templateUrl: './product-view-image-sizes-modal.html',
    styleUrl: './product-view-image-sizes-modal.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ICmsClientModule, TranslationModule, CommonModule]
})
export class ProductViewImageSizesModalComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProductViewImageSizesModal.IBlockDataContent;

    private modalRef = inject(ModalRef<ProductViewImageSizesModalComponent>);

    get headings(): string[] {
        if (this.client.isS) {
            return ['Panel Size', 'Thic...', 'Image Size With Bleed', 'Minimum Pixels @ 100 DPI', 'Recommended Pixels @ 150 DPI'];
        }
        return ['Panel Size', 'Thickness', 'Image Size With Bleed', 'Minimum Pixels @ 100 DPI', 'Recommended Pixels @ 150 DPI'];
    }

    cells = ['size', 'thickness', 'imageSizeWithBleed', 'minimumPixels', 'recommendedPixels'];

    protected close() {
        this.modalRef.close();
    }

    protected get points() {
        return this.content?.points || [];
    }
}
